import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Project, UserSubscription } from '../../models/types';
import { Clock, Eye, MapPin, Filter, Send, Calendar, MessageSquare, Bell, Search, X, Store } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ChatModal from '../../components/Chat/ChatModal';
import ProposalForm from '../../components/Proposal/ProposalForm';

interface ProjectWithChatInfo extends Project {
  hasProposal?: boolean;
  hasRequesterMessage?: boolean;
  requesterName?: string;
}

const MarketplacePage = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [projects, setProjects] = useState<ProjectWithChatInfo[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ProjectWithChatInfo[]>([]);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [userCountry, setUserCountry] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedChat, setSelectedChat] = useState<{
    projectId: string;
    requesterId: string;
    requesterName: string;
  } | null>(null);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectWithChatInfo | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching data for user:', currentUser.uid);

        // Fetch user's subscription
        const subscriptionDoc = await getDoc(doc(db, 'subscriptions', currentUser.uid));
        let userSubscription: UserSubscription | null = null;
        
        if (subscriptionDoc.exists()) {
          const data = subscriptionDoc.data() as UserSubscription;
          userSubscription = {
            ...data,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate()
          };
          console.log('User subscription:', userSubscription);
          setSubscription(userSubscription);
        } else {
          console.log('No subscription found - user has free access');
        }

        // Fetch user's profile for country
        const profileDoc = await getDoc(doc(db, 'providerProfiles', currentUser.uid));
        let providerCountry = '';
        if (profileDoc.exists()) {
          providerCountry = profileDoc.data().country;
          setUserCountry(providerCountry);
          console.log('User country:', providerCountry);
        }

        // Fetch active projects only
        const projectsRef = collection(db, 'projects');
        const baseQuery = query(
          projectsRef,
          where('status', '==', 'active')
        );

        const querySnapshot = await getDocs(baseQuery);
        console.log('Projects found:', querySnapshot.size);

        // Fetch user's proposals
        const proposalsRef = collection(db, 'proposals');
        const proposalsQuery = query(
          proposalsRef,
          where('providerId', '==', currentUser.uid)
        );
        const proposalsSnapshot = await getDocs(proposalsQuery);
        const userProposals = new Set(
          proposalsSnapshot.docs.map(doc => doc.data().projectId)
        );

        // Get accepted proposals for chat checking
        const acceptedProposals = new Map();
        proposalsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.status === 'accepted') {
            acceptedProposals.set(data.projectId, data);
          }
        });

        const projectsList: ProjectWithChatInfo[] = [];

        for (const projectDoc of querySnapshot.docs) {
          const projectData = projectDoc.data();
          
          // Skip projects from test users
          try {
            const projectOwnerDoc = await getDoc(doc(db, 'users', projectData.userId));
            if (projectOwnerDoc.exists()) {
              const ownerData = projectOwnerDoc.data();
              const isProjectFromTestUser = ownerData.isTestUser === true;
              
              // TEST user visibility rules:
              // - If current user is TEST: can see all projects (TEST and non-TEST)
              // - If current user is NOT TEST: can only see non-TEST projects
              if (isProjectFromTestUser && !isCurrentUserTest) {
                console.log(`Skipping TEST project ${projectDoc.id} - current user is not TEST`);
                continue; // Skip this TEST project for non-TEST users
              } else if (isProjectFromTestUser && isCurrentUserTest) {
                console.log(`Showing TEST project ${projectDoc.id} - current user is TEST`);
              } else {
                console.log(`Showing regular project ${projectDoc.id}`);
              }
            }
          } catch (error) {
            console.warn('Could not check test user status for project:', projectDoc.id);
            // Continue with project if we can't check test status
          }
          
          // Get requester name
          let requesterName = 'Project Requester';
          try {
            const requesterDoc = await getDoc(doc(db, 'users', projectData.userId));
            if (requesterDoc.exists()) {
              requesterName = requesterDoc.data().username || 'Project Requester';
            }
          } catch (error) {
            console.warn('Could not fetch requester name for project:', projectDoc.id);
          }

          // Check for messages from requester (only for accepted proposals)
          let hasRequesterMessage = false;
          if (acceptedProposals.has(projectDoc.id)) {
            try {
              const chatId = `${projectDoc.id}_${currentUser.uid}`;
              console.log(`Checking chat messages for project ${projectDoc.id}, chatId: ${chatId}`);
              
              const messagesRef = collection(db, `projects/${projectDoc.id}/chats/${chatId}/messages`);
              const messagesQuery = query(
                messagesRef,
                where('senderId', '==', projectData.userId)
              );
              
              const messagesSnap = await getDocs(messagesQuery);
              hasRequesterMessage = !messagesSnap.empty;
              
              console.log(`Project ${projectDoc.id}: Found ${messagesSnap.size} messages from requester`);
            } catch (chatError) {
              console.warn('Could not check for requester messages:', chatError);
            }
          }

          projectsList.push({
            id: projectDoc.id,
            ...projectData,
            createdAt: projectData.createdAt.toDate(),
            updatedAt: projectData.updatedAt.toDate(),
            hasProposal: userProposals.has(projectDoc.id),
            hasRequesterMessage,
            requesterName
          } as ProjectWithChatInfo);
        }

        // Apply subscription-based filtering
        let filteredProjects = projectsList;
        
        console.log('Applying subscription filters...');
        console.log('User subscription tier:', userSubscription?.tier || 'free');
        console.log('User country:', providerCountry);
        console.log('Total projects before filtering:', projectsList.length);

        // Check if current user is a TEST user
        let isCurrentUserTest = false;
        try {
          const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (currentUserDoc.exists()) {
            const currentUserData = currentUserDoc.data();
            isCurrentUserTest = currentUserData.isTestUser === true;
            console.log('Current user TEST status:', isCurrentUserTest);
          }
        } catch (error) {
          console.warn('Could not check current user TEST status:', error);
        }

        if (!userSubscription || userSubscription.tier === 'free') {
          // Free tier: Show all projects (no restrictions)
          console.log('Free tier: No filtering applied');
          filteredProjects = projectsList;
        } else if (userSubscription.tier === 'normal') {
          // Normal tier: Only projects from user's country
          if (providerCountry) {
            filteredProjects = projectsList.filter(project => {
              const matches = project.country === providerCountry;
              console.log(`Project ${project.id} (${project.country}) matches user country (${providerCountry}): ${matches}`);
              return matches;
            });
            console.log(`Normal tier: Filtered to ${filteredProjects.length} projects from ${providerCountry}`);
          } else {
            console.log('Normal tier: No user country found, showing all projects');
            filteredProjects = projectsList;
          }
        } else if (userSubscription.tier === 'gold' || userSubscription.tier === 'platinum') {
          // Gold/Platinum tier: All projects, but can filter by selected country
          if (selectedCountry) {
            filteredProjects = projectsList.filter(project => project.country === selectedCountry);
            console.log(`Gold/Platinum tier: Filtered to ${filteredProjects.length} projects from ${selectedCountry}`);
          } else {
            console.log('Gold/Platinum tier: Showing all projects');
            filteredProjects = projectsList;
          }
        }

        console.log('Final filtered projects:', filteredProjects.length);
        setProjects(filteredProjects);
        setFilteredProjects(filteredProjects);
      } catch (error) {
        console.error('Error fetching marketplace data:', error);
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, selectedCountry, t]);

  // Filter projects based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProjects(projects);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = projects.filter(project => {
      const nameMatch = project.name.toLowerCase().includes(query);
      const shortDescMatch = project.shortDescription.toLowerCase().includes(query);
      const longDescMatch = project.longDescription?.toLowerCase().includes(query) || false;
      
      return nameMatch || shortDescMatch || longDescMatch;
    });

    setFilteredProjects(filtered);
  }, [searchQuery, projects]);

  const clearSearch = () => {
    setSearchQuery('');
  };

  const openChat = (projectId: string, requesterId: string, requesterName: string) => {
    setSelectedChat({ projectId, requesterId, requesterName });
  };

  const closeChat = () => {
    setSelectedChat(null);
  };

  const openProposalForm = (project: ProjectWithChatInfo) => {
    setSelectedProject(project);
    setShowProposalForm(true);
  };

  const closeProposalForm = () => {
    setShowProposalForm(false);
    setSelectedProject(null);
  };

  const handleProposalSuccess = () => {
    console.log('Proposal submitted successfully, refreshing data...');
    // Close the modal and refresh data
    setShowProposalForm(false);
    setSelectedProject(null);
    
    // Refresh the marketplace data
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case '1':
        return t('project.timeline1');
      case '2-3':
        return t('project.timeline2-3');
      case '3-6':
        return t('project.timeline3-6');
      case '6+':
        return t('project.timeline6+');
      default:
        return t('project.timelineUnknown');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-700';
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const countries = [
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'BE', name: 'Belgium' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto container-padding section-padding">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="heading-md">{t('marketplace.title')}</h1>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          {/* Search Bar */}
          <div className="relative flex-grow sm:flex-grow-0 sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('marketplace.searchPlaceholder')}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Country filter - only show for Gold/Platinum tiers */}
          {(subscription?.tier === 'gold' || subscription?.tier === 'platinum') && (
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
              >
                <option value="">{t('marketplace.allCountries')}</option>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Search Results Info */}
      {searchQuery && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-body-sm text-blue-800">
            {t('marketplace.searchResults')} "{searchQuery}": {filteredProjects.length} {t('marketplace.projectsFound')}
          </p>
        </div>
      )}

      {/* Subscription Status Messages */}
      {!subscription && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-body-sm text-yellow-800">
            {t('marketplace.noSubscription')}
            <Link to="/business-plan" className="text-primary-600 font-medium ml-1 hover:underline">
              {t('marketplace.selectPlan')}
            </Link> 
            {t('marketplace.accessMore')}
          </p>
        </div>
      )}

      {subscription?.tier === 'normal' && userCountry && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-body-sm text-blue-800">
            {t('marketplace.normalTierLimit')} {countries.find(c => c.code === userCountry)?.name || userCountry}. 
            <Link to="/business-plan" className="text-primary-600 font-medium ml-1 hover:underline">
              {t('marketplace.upgradePlan')}
            </Link> 
            {t('marketplace.accessOtherCountries')}
          </p>
        </div>
      )}

      {filteredProjects.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md card-padding text-center">
          <h2 className="heading-sm mb-4">
            {searchQuery ? t('marketplace.noSearchResults') : t('marketplace.noProjects')}
          </h2>
          <p className="text-body text-gray-600 mb-4">
            {searchQuery 
              ? t('marketplace.tryDifferentSearch')
              : subscription?.tier === 'normal' 
                ? `${t('marketplace.upgradePlan')} ${t('marketplace.accessOtherCountries')}`
                : t('marketplace.checkLater')
            }
          </p>
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="inline-block mb-4 bg-gray-600 text-white px-4 sm:px-6 py-2 rounded-lg text-button hover:bg-gray-700 transition-colors"
            >
              {t('marketplace.clearSearch')}
            </button>
          )}
          {subscription?.tier === 'normal' && !searchQuery && (
            <Link
              to="/business-plan"
              className="inline-block bg-primary-600 text-white px-4 sm:px-6 py-2 rounded-lg text-button hover:bg-primary-700 transition-colors"
            >
              {t('marketplace.upgradePlan')}
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="card-padding">
                <div className="flex justify-between items-start mb-3">
                  <h2 className="heading-xs">{project.name}</h2>
                  <div className="flex items-center space-x-2">
                    {project.hasProposal && (
                      <span className="flex items-center px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                        <Send className="h-3 w-3 mr-1" />
                        {t('proposal.submitted')}
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                      {t(`project.${project.status}`)}
                    </span>
                  </div>
                </div>
                
                <p className="text-body-sm text-gray-600 mb-4 line-clamp-2">
                  {project.shortDescription}
                </p>
                
                <div className="flex flex-wrap gap-4 text-caption mb-4">
                  <div className="flex items-center">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span>{getTimelineLabel(project.timeline)}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span>{countries.find(c => c.code === project.country)?.name || project.country}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span>{format(project.createdAt, 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center">
                    <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span>{project.views} {t('project.views')}</span>
                  </div>
                </div>

                {/* Message notification */}
                {project.hasRequesterMessage && (
                  <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <div className="flex items-center text-primary-700">
                      <Bell className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      <span className="text-label">{t('marketplace.youHaveMessage')}</span>
                    </div>
                  </div>
                )}
                
                <div className="spacing-xs">
                  <Link
                    to={`/project/${project.id}`}
                    className="block w-full text-center py-2 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 transition-colors text-button-sm"
                  >
                    {t('project.viewDetails')}
                  </Link>
                  
                  {!project.hasProposal && (
                    <button
                      onClick={() => openProposalForm(project)}
                      className="w-full bg-orange-500 text-white py-2 rounded-md text-button-sm hover:bg-orange-600 transition-colors flex items-center justify-center"
                    >
                      <Send className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      {t('proposal.submit')}
                    </button>
                  )}
                  
                  {/* Chat button - only for accepted proposals */}
                  {project.hasProposal && (
                    <button
                      onClick={() => openChat(project.id, project.userId, project.requesterName || 'Project Requester')}
                      className={`w-full py-2 rounded-md text-button-sm transition-colors flex items-center justify-center ${
                        project.hasRequesterMessage
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      {project.hasRequesterMessage ? t('marketplace.youHaveMessage') : t('marketplace.chatWithRequester')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chat Modal */}
      {selectedChat && (
        <ChatModal
          projectId={selectedChat.projectId}
          chatId={`${selectedChat.projectId}_${currentUser?.uid}`}
          otherUserId={selectedChat.requesterId}
          otherUserName={selectedChat.requesterName}
          onClose={closeChat}
          allowMarketplaceChat={true}
        />
      )}

      {/* Proposal Form Modal */}
      {showProposalForm && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {t('proposal.submitFor')} "{selectedProject.name}"
                </h2>
                <button
                  onClick={closeProposalForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <ProposalForm
                project={selectedProject}
                onClose={closeProposalForm}
                onSuccess={handleProposalSuccess}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePage;